import axiosLib from 'axios'
import { BASE_API, BASE_PATH_API } from './apiConfig'
import { toast } from 'react-toastify'
import { localStorageService, toastFail } from '../helpers'
import { store } from '../store'
import { logout } from '../store/actions'
import { getRefreshTokenPath } from '../hooks/useAuthUtil'

export const baseURL = BASE_API + BASE_PATH_API

window.loggingout = null

const axios = axiosLib.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
})

window.cancelRequest = new AbortController()

const progressLoading = {
  data: {
    open: false,
    percent: 100,
    label: 'Loading...',
  },
  onChange: function (cb) {
    window.addEventListener('progress_upload', cb)
    return () => window.removeEventListener('progress_upload', cb)
  },
}

var updateProgress = data => {
  progressLoading.data = { ...progressLoading.data, ...data }
  // console.log('updateProgress', data)
  window.dispatchEvent(
    new CustomEvent('progress_upload', {
      detail: data,
    })
  )
}

window.refreshPromise = null

const handleLogout = async (fnc = '') => {
  if (!window.loggingout) {
    window.loggingout = new Promise(rs => {
      console.log('handleLogout', fnc)
      updateProgress({ open: false, percent: 0 })
      window.cancelRequest.abort()
      toastFail(fnc && fnc.status ? fnc.message : 'Token Expired')
      store.dispatch(logout())
      window.cancelRequest = new AbortController()
      rs()
    }).finally(() => {
      setTimeout(() => {
        window.loggingout = null
      }, 1500)
    })
  }

  return window.loggingout
}

const isExpiredToken = (name, tokenExp, timeBefore = 0) => {
  if (tokenExp === null) {
    console.log(name, null)
    return true
  }

  const now = Math.floor(Date.now() / 1000)
  const exp = now > tokenExp - timeBefore // 30 second = 0.5 minute

  return exp
}

const checkIsFile = (key, value, maxDeep) => {
  if (value instanceof File || value instanceof FileList) {
    return true
  } else if (Array.isArray(value)) {
    maxDeep += 1
    return value.some(d => checkContentsFile(d, maxDeep))
  }
  return false
}

function checkContentsFile(data, maxDeep) {
  if (maxDeep >= 10) {
    throw new Error('maximum dept recursive')
  }

  if (data instanceof FormData) {
    for (let [key, value] of data.entries()) {
      if (checkIsFile(key, value, maxDeep)) {
        return true
      }
    }
  } else if (data instanceof Object) {
    for (let key in data) {
      if (checkIsFile(key, data[key], maxDeep)) {
        return true
      }
    }
  }

  return false
}

const renewRequestConfig = async config => {
  if (!!window.refreshPromise && window.refreshPromise instanceof Promise) {
    const new_token = await window.refreshPromise
    if (typeof new_token === 'string') {
      config.headers['Authorization'] = 'Bearer ' + new_token
      return config
    } else if (window.loggingout) {
      return await window.loggingout
    }
  }

  return null
}

const public_path = ['/version', '/checkAutoUpdate']
const hide_progressbar = ['/company/information', 'cms/buyer/mysupplier/update']

const isHideProgerssbar = url => hide_progressbar.some(pg => pg.includes(url))

// Add a request interceptor
axios.interceptors.request.use(
  async config => {
    config.maxRedirects = 0

    if (window.loggingout && !public_path.some(p => config.url.endsWith(p))) {
      console.log('wait ', config.url)
      return await window.loggingout
    }

    if (config.url.endsWith('/login')) {
      window.loggingout = null
    } else {
      config.signal = window.cancelRequest.signal
    }

    const { access_token, access_token_exp, refresh_token } =
      localStorageService.getUser() || {}

    config.headers['Authorization'] = 'Bearer ' + access_token

    if (
      !window.refreshPromise &&
      isExpiredToken('access_token_exp', access_token_exp, 30)
    ) {
      window.refreshPromise = fetchRefreshToken(access_token, refresh_token)
    }

    const newConfig = await renewRequestConfig(config)
    if (newConfig) {
      config = newConfig
    }

    const isContentsFile = checkContentsFile(config.data)
    if (isContentsFile && !isHideProgerssbar(config.url)) {
      updateProgress({ open: true, percent: 0, label: 'Loading...' })
      return configUploadFile(config)
    } else {
      // console.log('is not contents file')
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

axios.interceptors.response.use(
  res => {
    const { config } = res
    if (checkContentsFile(config.data)) {
      updateProgress({ open: false })
    }
    localStorage.removeItem('ma')
    //console.log(res)
    return res
  },
  async err => {
    try {
      if (window.loggingout) {
        return await window.loggingout
      }

      const failedUrl = (err.config && err.config.url) || ''
      if (
        err.response.status == 401 &&
        err.config &&
        !err.config._retry &&
        !failedUrl.includes('/token/refresh') &&
        !failedUrl.endsWith('/login')
      ) {
        err.config._retry = true
        if (!window.refreshPromise) {
          const user = localStorageService.getUser() || {}
          window.refreshPromise = fetchRefreshToken(
            user.access_token,
            user.refresh_token
          )
        }
        const newConfig = await renewRequestConfig(err.config)
        if (newConfig && newConfig.headers) {
          return await axios.request(newConfig)
        }
      }

      let { data: resData } = err.response || {}
      if (
        err.config &&
        err.config.data &&
        err.config.data instanceof FormData
      ) {
        updateProgress({ open: false, percent: 0 })
      }

      if (err && err.code !== 'ERR_CANCELED') {
        if (
          (err.code && err.code === 'ECONNABORTED') ||
          (err.message && err.message.includes('timeout'))
        ) {
          updateProgress({ open: false, percent: 0 })
          return Promise.reject({
            response: { data: { result: 'Request timeout' } },
          })
        }

        if (resData && resData.result) {
          const { isMaMode } = resData.result
          if (isMaMode) {
            localStorage.setItem('ma', JSON.stringify(resData.result))
            window.location.replace('/ma')
          } else {
            localStorage.removeItem('ma')
          }
        }

        // console.log({ err })

        if ('response' in err) {
          return Promise.reject(err)
        }
      }
    } catch (e) {
      console.log(e)
      return Promise.reject(err)
    }
  }
)

const axiosSuccess = (res, configService) => {
  if (configService.isShowToastSuccess) {
    toast.success(res.data.message)
  }

  return res.data.result || res.data.results || res || null
}

const axiosNewsSuccess = (res, configService) => {
  if (configService.isShowToastSuccess) {
    toast.success(res.data.message)
  }

  return res || null
}

const axiosSuccessUpload = (res, configService) => {
  if (configService.isShowToastSuccess) {
    toast.success(res.data.message)
  }
  return res.data || res.data.results || res || null
}

const axiosError = async error => {
  // console.log(error, configService)

  if (!error.response) {
    return ''
  }

  let message = ''
  const { data, status } = error.response

  if (
    (data &&
      status === 400 &&
      ['Account Invalid', 'Token Expired'].includes(data.message)) ||
    (status === 403 && ['Access denied'].includes(data.message))
  ) {
    await handleLogout(data)
  } else if (status === 401 || data.status === '401') {
    await handleLogout('status 401')
  } else if (status === 504) {
    message = 'Request timeout'
  } else if (data && status !== 401 && typeof data === 'object') {
    if (data && data.status && data.message) {
      message = data.message
    } else if (data.result) {
      if (['4000: permission denial', 'unauthorized'].includes(data.result)) {
        message = 'Unauthorized access'
        updateProgress({ open: false, percent: 0 })
      } else if (data.result === 'Canceled') {
        message = 'Request canceled'
      } else {
        message = data.result
      }
    } else {
      message = 'Server Error'
    }
  } else {
    message = 'Server Error'
  }

  // console.log({ error })

  if (message && !window.loggingout) {
    toastFail(message)
  }
  // throw error
}

const fetchRefreshToken = async () => {
  const refresh_url = getRefreshTokenPath()
  const user = localStorageService.getUser() || {}
  return axiosLib
    .create({
      baseURL: baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .post(refresh_url, {
      refresh_token: user.refresh_token,
      access_token: user.access_token,
    })
    .then(async res => {
      if (res.status === 201) {
        console.log('fetchRefreshToken res201', res)
        axios.defaults.headers.common.Authorization = `Bearer ${res.data.access_token}`
        // store.dispatch(setUser({ ...user, ...res.data }))
        localStorageService.setUser({ ...user, ...res.data })
        return res.data.access_token
      } else {
        console.log('fetchRefreshToken res', res)
        return await handleLogout()
      }
    })
    .catch(async err => {
      console.log('fetchRefreshToken c', err)
      return await handleLogout()
    })
    .finally(() => {
      window.refreshPromise = null
    })
}

const configUploadFile = (config = {}) => {
  return {
    ...config,
    // maxContentLength: 50 * 1024 * 1024,
    // maxBodyLength: 50 * 1024 * 1024,
    headers: {
      ...(config.headers || {}),
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress(progressEvent) {
      if (window.loggingout) {
        updateProgress({ open: false, percent: 0, label: '' })
      } else if (!window.refreshPromise) {
        const { loaded, total } = progressEvent
        const percent = Math.floor((loaded * 100) / total)

        if (percent <= 100) {
          updateProgress({
            open: !!progressLoading.data.open,
            percent,
            label: 'Loading...',
          })
        }
      }
    },
    // signal: window.cancelRequest.signal,
    // timeout: 10 * 60 * 1000, // 10 minute
  }
}

const configDownloadFile = () => {
  return {
    responseType: 'arraybuffer',
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
  }
}

const axiosService = (type, url, params, configService = {}) => {
  const configVerifyToken = {
    headers: {
      //params is token from path url
      Authorization: 'Bearer ' + params,
    },
  }
  configService = {}
  switch (type) {
    case 'get':
      return axios
        .get(url, { params }, configService)
        .then(res => {
          return axiosSuccess(res, configService)
        })
        .catch(error => axiosError(error, configService))
    case 'getNew':
      return axios
        .get(url, { params })
        .then(res => {
          return axiosNewsSuccess(res, configService)
        })
        .catch(error => axiosError(error, configService))
    case 'getForgotPassword':
      return axios
        .get(url, { params })
        .then(res => {
          return res
        })
        .catch(error => {
          return error
        })
    case 'verifyToken':
      return axios
        .get(url, configVerifyToken)
        .then(res => {
          return axiosSuccess(res, configService)
        })
        .catch(error => {
          return error
        })

    case 'getuser':
      return axios
        .get(url, { params })
        .then(res => {
          return axiosSuccess(res, configService)
        })
        .catch(error => axiosError(error, configService))

    case 'post':
      return axios
        .post(url, params)
        .then(res => {
          return axiosSuccess(res, configService)
        })
        .catch(error => axiosError(error, configService))

    case 'downloadFile':
      return axios
        .get(url, configDownloadFile())
        .then(res => {
          return axiosSuccess(res, configService)
        })
        .catch(error => axiosError(error, configService))

    case 'uploadFile':
      return axios
        .postForm(url, params)
        .then(res => {
          return axiosSuccess(res, configService)
        })
        .catch(error => axiosError(error, configService))

    case 'uploadFileSupplier':
      return axios
        .postForm(url, params)
        .then(res => {
          return axiosSuccessUpload(res, configService)
        })
        .catch(error => axiosError(error, configService))

    case 'exportFile':
      return axios
        .get(url, { responseType: 'blob' })
        .then(res => {
          return axiosSuccess(res, configService)
        })
        .catch(error => axiosError(error, configService))

    case 'put':
      return axios
        .put(url, params)
        .then(res => {
          return axiosSuccess(res, configService)
        })
        .catch(error => axiosError(error, configService))
    // createEvent
    case 'createEvent':
      return axios
        .put(url, params)
        .then(res => {
          return res
        })
        .catch(error => axiosError(error, configService))
    case 'delete':
      return axios
        .delete(url, params)
        .then(res => {
          return axiosSuccess(res, configService)
        })
        .catch(error => axiosError(error, configService))
    case 'postFormData':
      return axios
        .postForm(url, params)
        .then(res => {
          // console.log('res', res)
          return axiosSuccess(res, configService)
        })
        .catch(error => axiosError(error, configService))
    case 'putFormData':
      return axios
        .putForm(url, params)
        .then(res => {
          // console.log('res', res)
          return axiosSuccess(res, configService)
        })
        .catch(error => axiosError(error, configService))
    case 'putFormDataRegis':
      return axios
        .putForm(url, params, configUploadFile())
        .then(res => {
          // console.log('res', res)
          return axiosSuccess(res, configService)
        })
        .catch(error => axiosError(error, configService))
    case 'postRequestTec':
      return axios({
        method: 'post',
        url: url,
        data: params,
      })
        .then(res => {
          return res
        })
        .catch(error => axiosError(error, configService))
    case 'postUnsealTec':
      return axios({
        method: 'post',
        url: url,
        data: params,
      })
        .then(res => {
          return res
        })
        .catch(error => axiosError(error, configService))
    case 'acceptResponse':
      return axios({
        method: 'put',
        url: url,
        data: params,
      })
        .then(res => {
          return res
        })
        .catch(error => {
          return error.response
        })
    case 'onClickRejectTimeOut':
      return axios({
        method: 'put',
        url: url,
        data: params,
      })
        .then(res => {
          return res
        })
        .catch(error => {
          return error.response
        })
    case 'putFacade':
      return axios({
        method: 'put',
        url: url,
        data: params,
      })
        .then(res => {
          return res
        })
        .catch(error => {
          return error.response
        })
    default:
      return false
  }
}

export default (configService = {}) => {
  configService = {}
  return {
    get: ({ url, params }) => axiosService('get', url, params, configService),
    getNew: ({ url, params }) =>
      axiosService('getNew', url, params, configService),
    getUser: ({ url, params }) =>
      axiosService('getuser', url, params, configService),
    post: ({ url, params }) => axiosService('post', url, params, configService),
    put: ({ url, params }) => axiosService('put', url, params, configService),
    //acceptResponse
    acceptResponse: ({ url, params }) =>
      axiosService('acceptResponse', url, params, configService),
    //onClickRejectTimeOut
    onClickRejectTimeOut: ({ url, params }) =>
      axiosService('onClickRejectTimeOut', url, params, configService),
    //putFacade
    putFacade: ({ url, params }) =>
      axiosService('putFacade', url, params, configService),
    // createEvent
    createEvent: ({ url, params }) =>
      axiosService('createEvent', url, params, configService),
    downloadFile: ({ url, params }) =>
      axiosService('downloadFile', url, params, configService),
    uploadFile: ({ url, formData }) =>
      axiosService('uploadFile', url, formData, configService),
    uploadFileSupplier: ({ url, formData }) =>
      axiosService('uploadFileSupplier', url, formData, configService),
    exportFile: ({ url, params }) =>
      axiosService('exportFile', url, params, configService),
    delete: ({ url, params }) =>
      axiosService('delete', url, params, configService),
    verifyToken: ({ url, token }) =>
      axiosService('verifyToken', url, token, configService),
    postFormData: ({ url, params }) =>
      axiosService('postFormData', url, params, configService),
    putFormData: ({ url, params }) =>
      axiosService('putFormData', url, params, configService),
    putFormDataRegis: ({ url, params }) =>
      axiosService('putFormDataRegis', url, params, configService),
    postRequestTec: ({ url, params }) =>
      axiosService('postRequestTec', url, params, configService),
    postUnsealTec: ({ url, params }) =>
      axiosService('postUnsealTec', url, params, configService),
    getForgotPassword: ({ url, params }) =>
      axiosService('getForgotPassword', url, params, configService),
  }
}

export { axios, progressLoading, updateProgress }
